// The AI usage every chat shows by its message box, as the old AI Chat did and Claude does: a ring of the AI credits
// used with the org's usage limits on hover, a ring of how full this chat's context is, the last answer's tokens, and
// "out of credits" said before the user types rather than after they send.
//
// One read per game for the whole page (GET /v1/web/chat/credits: the usage gate's own credit check and the org's
// spending limits), shared by every chat on it and read again after each answer, so the rings follow what the answer
// cost. Turns report themselves here from streamChat (turnSettled), so no chat has to wire anything. A read that fails
// shows nothing: the server still refuses a turn it cannot pay for, and says why.

import { getChatCredits } from '@/services/chat/ChatCreditsService.js';

const FRESH_MS = 30_000;
// The ledger is debited as the answer completes; read a moment later so the ring has moved.
const AFTER_TURN_MS = 1_000;

const EMPTY = { key: null, credits: null, limits: [], at: 0 };
let state = EMPTY;
let inflight = null;
let afterTurn = null;
const listeners = new Set();

const publish = (next) => {
    state = next;
    listeners.forEach((fn) => fn());
};

export const subscribeCredits = (fn) => {
    listeners.add(fn);
    return () => listeners.delete(fn);
};

export const creditsSnapshot = () => state;

/** Reads the credits and limits for `key` (the game) unless a fresh read is already there or on its way. */
export const loadCredits = (key, { force = false } = {}) => {
    if (!key) return Promise.resolve(null);
    const fresh = state.key === key && Date.now() - state.at < FRESH_MS;
    if (!force && (fresh || inflight?.key === key)) return inflight?.promise ?? Promise.resolve(state.credits);
    const promise = getChatCredits()
        .catch(() => null)
        .then((read) => {
            if (inflight?.promise === promise) inflight = null;
            const credits = read?.credits ?? null;
            publish({ key, credits, limits: Array.isArray(read?.limits) ? read.limits : [], at: Date.now() });
            return credits;
        });
    inflight = { key, promise };
    if (state.key !== key) publish({ ...EMPTY, key });
    return promise;
};

// ── The turns, as streamChat reports them ──────────────────────────────────────────────────────────────────────────

const turnListeners = new Set();

/** (turn) => void with { type: 'settled', usage, context }. Returns the unsubscribe. */
export const subscribeTurns = (fn) => {
    turnListeners.add(fn);
    return () => turnListeners.delete(fn);
};

/**
 * A turn ended. `completed` is intel's `completed` frame (its `usage` and `context`), or null when the turn was
 * stopped, failed or refused. The credits are read again.
 */
export const turnSettled = (completed = null) => {
    const turn = { type: 'settled', usage: completed?.usage ?? null, context: completed?.context ?? null };
    turnListeners.forEach((fn) => fn(turn));
    clearTimeout(afterTurn);
    afterTurn = setTimeout(() => { if (state.key) loadCredits(state.key, { force: true }); }, AFTER_TURN_MS);
};

/** Only for tests: forget everything read. */
export const resetCredits = () => {
    state = EMPTY;
    inflight = null;
    clearTimeout(afterTurn);
    listeners.clear();
    turnListeners.clear();
};

// ── What to show ──────────────────────────────────────────────────────────────────────────────────────────────────

/** "$4.20", "$0.05", "<$0.01" from micro-USD. */
export const formatMicroUsd = (micro) => {
    const usd = Math.max(0, Number(micro) || 0) / 1_000_000;
    if (usd > 0 && usd < 0.01) return '<$0.01';
    return `$${usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/** "820", "13k", "1.5k", "2.1M". */
export const formatTokens = (n) => {
    const v = Math.max(0, Math.round(Number(n) || 0));
    if (v < 1_000) return String(v);
    if (v < 1_000_000) return `${(v / 1_000).toFixed(v < 10_000 ? 1 : 0).replace(/\.0$/, '')}k`;
    return `${(v / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
};

/** The last answer's tokens as the old chat showed them, or null when intel sent none. */
export const tokensOf = (usage) => {
    const input = Number(usage?.input_tokens) || 0;
    const output = Number(usage?.output_tokens) || 0;
    if (!input && !output) return null;
    return { input, output, label: `${formatTokens(input)} in · ${formatTokens(output)} out` };
};

// A share used, as Claude colours its meters: the theme's blue, amber past three quarters, red past nine tenths.
const toneOf = (share) => {
    if (share >= 0.9) return 'low';
    if (share >= 0.75) return 'warn';
    return 'good';
};

/**
 * The credits: how much of what the user spends from (their allotment, else the game's credits) is used. Red when
 * they cannot spend. An org never refused (LNG's own) sees its numbers in a neutral grey, since running low never
 * stops it. Null with nothing read.
 */
export const meterOf = (credits) => {
    if (!credits) return null;
    const total = Math.max(0, credits.totalMicroUsd || 0);
    const left = Math.max(0, credits.remainingMicroUsd || 0);
    const share = total > 0 ? Math.min(1, Math.max(0, 1 - left / total)) : 0;
    let tone = toneOf(share);
    if (credits.exempt) tone = 'neutral';
    else if (!credits.canSpend) tone = 'low';
    const perUser = credits.mode === 'PER_USER';
    return {
        frac: share,
        percent: Math.round(share * 100),
        tone,
        hasCap: total > 0,
        scope: perUser ? 'Your allotment' : 'This game’s credits',
        left: formatMicroUsd(left),
        total: formatMicroUsd(total),
        used: formatMicroUsd(credits.usedMicroUsd),
        empty: perUser ? 'No allotment assigned yet' : 'No credits added yet',
        exempt: !!credits.exempt,
    };
};

const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** "Resets at 10:00 PM" today, else "Resets Wed 5:30 AM", in the viewer's own time. */
export const resetLabel = (iso, now = new Date()) => {
    const at = new Date(iso);
    if (Number.isNaN(at.getTime())) return null;
    const time = at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    if (sameDay(at, now)) return `Resets at ${time}`;
    return `Resets ${at.toLocaleDateString('en-US', { weekday: 'short' })} ${time}`;
};

const LIMIT_LABEL = { HOUR: 'Hourly limit', DAY: 'Daily limit' };

/** The org's spending limits as Claude lists its usage limits: what, when it resets, how much is used. */
export const limitsOf = (limits, now = new Date()) => (Array.isArray(limits) ? limits : [])
    .filter((l) => LIMIT_LABEL[l?.window])
    .map((l) => {
        const percent = Math.min(100, Math.max(0, Math.round(Number(l.usedPercent) || 0)));
        return {
            key: l.window,
            label: LIMIT_LABEL[l.window],
            percent,
            tone: toneOf(percent / 100),
            resets: resetLabel(l.resetsAt, now),
        };
    });

/**
 * How full this chat's context is: the conversation the model is sent against intel's history budget. Past it,
 * older messages are summarized, so it never blocks the chat; `summarized` says that has happened.
 */
export const contextOf = (context) => {
    const used = Math.max(0, Number(context?.used_tokens) || 0);
    const limit = Math.max(0, Number(context?.limit_tokens) || 0);
    if (!limit || !used) return null;
    const share = Math.min(1, used / limit);
    return {
        frac: share,
        percent: Math.round(share * 100),
        tone: share >= 0.9 ? 'warn' : 'neutral',
        used: formatTokens(used),
        limit: formatTokens(limit),
        summarized: !!context.summarized,
    };
};

/**
 * Why the user cannot chat, and what to do about it, or null when they can. `canManage`: they can give credits or
 * allotments themselves, so they are told where; everyone else is told whom to ask.
 */
export const creditsNotice = (credits, canManage = false) => {
    if (!credits || credits.canSpend) return null;
    switch (credits.reason) {
        case 'NO_GAME':
            return 'Select a game first: AI is paid for from a game’s AI credits.';
        case 'NO_ALLOTMENT':
            return canManage
                ? 'This game gives each person their own AI credits, and you don’t have an allotment yet. Assign yourself one in AI Credits, under User Limits, to start.'
                : 'You don’t have AI credits on this game yet. Ask a game owner to give you an allotment.';
        case 'ALLOTMENT_USED':
            return canManage
                ? 'You’ve used your AI credit allotment on this game. Raise it in AI Credits, under User Limits, to continue.'
                : 'You’ve used the AI credits assigned to you on this game. Ask a game owner to raise your allotment.';
        default:
            return canManage
                ? 'This game is out of AI credits. Add credits in AI Credits to continue.'
                : 'This game is out of AI credits. Ask a game owner to add more.';
    }
};
